import * as THREE from 'three';
import { PROJECTS } from './data/projects.js';
import { TOPICS, CATEGORY_ORDER } from './data/topics.js';
import { createSky } from './world/sky.js';
import { createLights } from './world/lights.js';
import { createCelestial } from './world/celestial.js';
import { createComposer } from './world/postfx.js';
import { FACTORIES, applyFeaturedTreatment } from './world/islands.js';
import { buildGroundDetail } from './world/helpers.js';
import { createUfo, setBeamLength } from './world/ufo.js';
import { createControls } from './controls.js';
import { buildMenu, markVisited, clearActive, setActiveChip } from './ui/menu.js';
import { createMinimap } from './ui/minimap.js';
import { buildLabels, updateLabels } from './ui/labels.js';
import { initPanel, openPanel, isPanelOpen, isFullPanelOpen } from './ui/panel.js';

// ============================================================================
// Renderer
// ============================================================================
const canvas = document.getElementById('scene');
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.95;
renderer.outputColorSpace = THREE.SRGBColorSpace;

// ============================================================================
// Scene
// ============================================================================
const scene = new THREE.Scene();
scene.fog = new THREE.FogExp2(0xf2c890, 0.0006);

const sunDir = new THREE.Vector3(-0.55, 0.32, -0.78).normalize();

const sky = createSky(sunDir);
scene.add(sky.mesh);

const lights = createLights(sunDir);
scene.add(lights.hemi, lights.sun);

const celestial = createCelestial(sunDir);
scene.add(celestial.group);

// ============================================================================
// Camera
// ============================================================================
const camera = new THREE.PerspectiveCamera(68, window.innerWidth / window.innerHeight, 0.5, 4000);
camera.rotation.order = 'YXZ';

// ============================================================================
// Islands
// ============================================================================
const islandObjects = [];
const variantCounter = {};
PROJECTS.forEach((project, i) => {
  const topic = TOPICS[project.topic];
  const variantIdx = variantCounter[project.topic] || 0;
  variantCounter[project.topic] = variantIdx + 1;
  const mesh = FACTORIES[project.topic](topic.color, i * 7, variantIdx);
  mesh.position.set(...project.pos);
  mesh.userData.project = project;
  mesh.traverse(c => { c.userData.project = project; });
  if (project.featured) applyFeaturedTreatment(mesh, topic.color);
  scene.add(mesh);
  islandObjects.push({ project, mesh });
});

const luxtts = islandObjects.find(o => o.project.id === 'luxtts');
if (luxtts) luxtts.mesh.scale.setScalar(1.1);

buildGroundDetail(scene, islandObjects);

// ============================================================================
// UFO (the player avatar)
// ============================================================================
const ufo = createUfo();
scene.add(ufo);

// ============================================================================
// UI
// ============================================================================
buildLabels(islandObjects);

let activeTopic = 'all';
function setTopicFilter(topic) {
  activeTopic = topic;
  setActiveChip(topic);
  islandObjects.forEach(o => {
    const match = topic === 'all' || o.project.topic === topic;
    o.mesh.traverse(c => {
      if (!c.material) return;
      const mats = Array.isArray(c.material) ? c.material : [c.material];
      mats.forEach(m => {
        if (!m.userData) m.userData = {};
        if (m.userData._origOpacity === undefined) {
          m.userData._origOpacity = m.opacity ?? 1;
          m.userData._origTransparent = m.transparent;
        }
        m.transparent = true;
        m.opacity = match ? m.userData._origOpacity : 0.12;
      });
    });
  });
}

buildMenu(
  (id) => focusProject(id),
  (topic) => setTopicFilter(topic)
);

const minimap = createMinimap(islandObjects, camera, () => activeTopic, (id) => focusProject(id));

const controls = createControls(canvas, isPanelOpen);

// Initial UFO position — over the central cottage so the user sees both
controls.state.ufoPos.set(0, 180, 320);

// Click on canvas → raycast → focus
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
controls.state.onClickIsland = (x, y) => {
  if (isPanelOpen()) return;
  pointer.x = (x / window.innerWidth) * 2 - 1;
  pointer.y = -(y / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(pointer, camera);
  const hits = raycaster.intersectObjects(islandObjects.map(o => o.mesh), true);
  if (hits.length) {
    const p = hits[0].object.userData.project;
    if (p) focusProject(p.id);
  }
};

window.addEventListener('keydown', e => {
  if (isPanelOpen()) return;
  if ((e.key >= '1' && e.key <= '9') || e.key === '0') {
    const idx = e.key === '0' ? 9 : parseInt(e.key) - 1;
    const topics = ['all', ...CATEGORY_ORDER];
    if (idx < topics.length) setTopicFilter(topics[idx]);
  }
});

// ============================================================================
// Focus / beam flow
// ============================================================================
// Phases: approach (UFO flies to island top) → extend (beam grows) → open (panel up)
// Close panel → retract (beam shrinks) → done (controls unlocked)
let focusAnimation = null;

const CAM_DIST_FOCUS = 95;  // pulled-back cinematic distance when inspecting an island

function focusProject(id) {
  const p = PROJECTS.find(x => x.id === id);
  if (!p) return;
  markVisited(id);

  const target = new THREE.Vector3(...p.pos);
  // Hover higher above the island so the camera can sit behind and look down
  const ufoDest = target.clone();
  ufoDest.y += 60;
  const beamMax = Math.max(8, ufoDest.y - target.y - 4);

  const startYaw = controls.state.yaw;
  const startPitch = controls.state.pitch;
  const destYaw = startYaw;
  const destPitch = -0.45;

  controls.state.locked = true;
  controls.state.velocity.set(0, 0, 0);

  focusAnimation = {
    startPos: controls.state.ufoPos.clone(),
    destPos: ufoDest,
    startYaw, destYaw,
    startPitch, destPitch,
    startCamDist: controls.state.camDist,
    destCamDist: CAM_DIST_FOCUS,
    target,
    t: 0,
    duration: 1.4,
    phase: 'approach',
    projectId: id,
    beamMax,
    beamLen: 0
  };
}

function retractBeam() {
  if (focusAnimation && (focusAnimation.phase === 'extend' || focusAnimation.phase === 'open')) {
    focusAnimation.phase = 'retract';
    focusAnimation.t = 0;
    focusAnimation.duration = 0.4;
  } else {
    setBeamLength(ufo, 0);
    controls.state.locked = false;
    focusAnimation = null;
  }
}

initPanel(() => {
  clearActive();
  retractBeam();
});

// ============================================================================
// UFO arrival opening — drops in from above to spawn position
// ============================================================================
const openingStart = controls.state.ufoPos.clone();
openingStart.y += 380;
controls.state.ufoPos.copy(openingStart);
const openingEnd = new THREE.Vector3(0, 180, 320);
let openingT = 0;
const openingDuration = 2.2;
let openingActive = true;

// ============================================================================
// Post-processing
// ============================================================================
const fx = createComposer(renderer, scene, camera);

// ============================================================================
// Animation loop
// ============================================================================
const clock = new THREE.Clock();
let time = 0;
let frame = 0;

function animate() {
  requestAnimationFrame(animate);
  if (isFullPanelOpen()) return;
  const dt = Math.min(clock.getDelta(), 0.1);
  time += dt;
  frame++;

  // --- Camera/UFO state machine ---
  if (openingActive) {
    openingT += dt;
    const u = Math.min(openingT / openingDuration, 1);
    const ease = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
    controls.state.ufoPos.lerpVectors(openingStart, openingEnd, ease);
    controls.update(dt, camera, ufo);
    if (u >= 1) openingActive = false;
  } else if (focusAnimation) {
    focusAnimation.t += dt;
    const fa = focusAnimation;

    if (fa.phase === 'approach') {
      const u = Math.min(fa.t / fa.duration, 1);
      const ease = u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;
      controls.state.ufoPos.lerpVectors(fa.startPos, fa.destPos, ease);
      let dy = fa.destYaw - fa.startYaw;
      while (dy > Math.PI) dy -= Math.PI * 2;
      while (dy < -Math.PI) dy += Math.PI * 2;
      controls.state.yaw = fa.startYaw + dy * ease;
      controls.state.pitch = fa.startPitch + (fa.destPitch - fa.startPitch) * ease;
      // Pull camera away
      controls.state.camDist = fa.startCamDist + (fa.destCamDist - fa.startCamDist) * ease;
      if (u >= 1) {
        fa.phase = 'extend';
        fa.t = 0;
        fa.duration = 0.5;
      }
    } else if (fa.phase === 'extend') {
      const u = Math.min(fa.t / fa.duration, 1);
      fa.beamLen = u * fa.beamMax;
      setBeamLength(ufo, fa.beamLen);
      if (u >= 1) {
        fa.phase = 'open';
        openPanel(fa.projectId);
      }
    } else if (fa.phase === 'open') {
      // Idle while panel is open — beam stays extended
      setBeamLength(ufo, fa.beamMax);
    } else if (fa.phase === 'retract') {
      const u = Math.min(fa.t / fa.duration, 1);
      fa.beamLen = (1 - u) * fa.beamMax;
      setBeamLength(ufo, fa.beamLen);
      // Pull camera back in toward UFO
      controls.state.camDist = fa.destCamDist + (controls.CAM_DIST_DEFAULT - fa.destCamDist) * u;
      if (u >= 1) {
        setBeamLength(ufo, 0);
        focusAnimation = null;
        controls.state.locked = false;
        controls.state.camDist = controls.CAM_DIST_DEFAULT;
      }
    }

    controls.update(dt, camera, ufo);
  } else {
    controls.update(dt, camera, ufo);
  }

  // --- Per-island animations ---
  islandObjects.forEach(o => {
    if (o.mesh.userData.halo) {
      const s = 1 + Math.sin(time * 1.2) * 0.06;
      o.mesh.userData.halo.scale.set(s, s, 1);
      o.mesh.userData.halo.material.opacity = 0.14 + Math.sin(time * 1.2) * 0.05;
    }
    if (o.project.topic === 'quantum') {
      const u = o.mesh.userData;
      if (u.spin) { u.spin.rotation.y = time * 0.6; u.spin.rotation.x = time * 0.32; }
      if (u.shell) { u.shell.rotation.y = -time * 0.4; u.shell.rotation.z = time * 0.2; }
      if (u.outer) { u.outer.rotation.y = time * 0.18; u.outer.rotation.x = -time * 0.12; }
    }
    if (o.project.topic === 'sim' && o.mesh.userData.orbits) {
      o.mesh.userData.orbits.forEach(b => {
        const u = b.userData;
        u.angle += u.speed * u.dir * dt;
        b.position.x = Math.cos(u.angle) * u.radius;
        b.position.z = Math.sin(u.angle) * u.radius;
      });
    }
    if (o.project.topic === 'audio' && o.mesh.userData.bars) {
      o.mesh.userData.bars.forEach(b => {
        const u = b.userData;
        const scale = 1 + Math.sin(time * u.freq + u.phase) * 0.32;
        b.scale.y = scale;
        b.position.y = 4.5 + (u.baseHeight * scale) / 2;
      });
    }
    if (o.project.id === 'profile') {
      const u = o.mesh.userData;
      if (u.weatherVane) u.weatherVane.rotation.y = time * 0.4;
      if (u.magicRing) u.magicRing.rotation.y = time * 0.3;
    }
    if (o.project.topic === 'ai' && o.mesh.userData.aiOrbiters) {
      o.mesh.userData.aiOrbiters.forEach(b => {
        const u = b.userData;
        u.angle += u.speed * dt;
        b.position.x = Math.cos(u.angle) * u.radius;
        b.position.z = Math.sin(u.angle) * u.radius;
      });
      if (o.mesh.userData.aiCore) o.mesh.userData.aiCore.rotation.y = time * 0.3;
    }
    if (o.project.topic === 'cv' && o.mesh.userData.cvEye) {
      const a = time * 0.6;
      o.mesh.userData.cvEye.position.x = Math.cos(a) * 3.6;
      o.mesh.userData.cvEye.position.z = Math.sin(a) * 3.6;
    }
    if (o.mesh.userData.beacon) {
      o.mesh.userData.beacon.rotation.y = time * 1.4;
    }
  });

  // UFO under-light flicker (subtle pulse)
  if (ufo.userData.lights) {
    const pulse = 0.7 + Math.sin(time * 4) * 0.3;
    ufo.userData.lights.forEach((l, i) => {
      l.material.color.setRGB(1, 0.82 + Math.sin(time * 4 + i) * 0.1 * pulse, 0.4);
    });
  }

  celestial.update(time);

  if (frame % 2 === 0) updateLabels(islandObjects, camera, activeTopic);
  if (frame % 4 === 0) minimap.draw(controls.state.yaw);

  fx.setTime(time);
  fx.composer.render();
}

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  fx.resize();
});

setTimeout(() => document.getElementById('loading').classList.add('hide'), 900);
animate();
